import { z } from 'zod';
import {
  currencyCodeSchema,
  emailSchema,
  isoDateSchema,
  localeSchema,
  uuidSchema,
} from './index';

export { currencyCodeSchema, emailSchema, isoDateSchema, localeSchema, uuidSchema };

// ==================== Participant ====================

export const participantSchema = z.object({
  id: uuidSchema,
  auth_user_id: uuidSchema.nullable(),
  display_name: z.string().min(1).max(100),
  email: emailSchema.nullable(),
  email_normalized: z.string().nullable(),
  avatar_url: z.string().url().nullable(),
  preferred_currency: currencyCodeSchema.nullable(),
  // **De lectura va ancho a propósito, y de escritura estrecho** (ver
  // `participantUpdateSchema`, abajo). No es descuido: son dos preguntas
  // distintas.
  //
  // Lo que la web *escribe* tiene que ser uno de los suyos. Lo que *lee* lo
  // pudo escribir otra superficie: el cliente nativo salió con portugués el
  // 2026-08-21 (#48) y la web no lo tiene. Con `localeSchema` aquí,
  // `fetchMyProfile` hacía `safeParse` → fallo → **«Perfil con formato
  // inesperado»**, o sea que a esa persona no le carga la web entera. No es
  // que la vea en otro idioma: es que no entra.
  //
  // Y estrechar aquí no compraba nada, porque `normalizeToSupported` ya sabe
  // qué hacer con un código que no soporta —para eso existe—. Eran dos guardas
  // para lo mismo, y la de más abajo hacía el trabajo mientras ésta tiraba la
  // sesión.
  preferred_locale: z.string().nullable(),
  phone: z.string().nullable(),
  /**
   * **Sin esto, la puerta del consentimiento no se cierra nunca.**
   *
   * `fetchMyProfile` hace `select('*')`, así que la columna llega; pero luego
   * pasa la fila por este esquema, y un `z.object` **descarta lo que no
   * declara**. `ConsentGate` leía `profile.gdpr_consent_at`, veía `undefined`
   * pase lo que pase, y volvía a pedir el consentimiento aunque acabaras de
   * darlo: el botón guardaba en la base de datos, refrescaba el perfil, y el
   * perfil volvía otra vez sin el campo.
   *
   * Visto desde fuera, «pulso y no hace nada». Y sin un solo error registrado,
   * porque nada fallaba.
   *
   * Va **obligatoria y anulable**, no opcional: los dos sitios que parsean con
   * este esquema hacen `select('*')`, así que la columna siempre viene. Si un
   * día dejara de venir, lo correcto es que el parseo falle a gritos —«Perfil
   * con formato inesperado»— y no que el campo se evapore otra vez.
   */
  gdpr_consent_at: z.string().nullable(),
  is_super_admin: z.boolean(),
  is_demo: z.boolean().default(false),
  created_by: uuidSchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
  last_active_at: z.string().nullable().optional(),
});

export type Participant = z.infer<typeof participantSchema>;

export const participantUpdateSchema = z.object({
  display_name: z.string().min(1).max(100).optional(),
  avatar_url: z.string().url().nullable().optional(),
  preferred_currency: currencyCodeSchema.optional(),
  preferred_locale: localeSchema.optional(),
  phone: z.string().max(50).nullable().optional(),
});

export type ParticipantUpdate = z.infer<typeof participantUpdateSchema>;

// ==================== Auth ====================

export const signInWithEmailSchema = z.object({
  email: emailSchema,
  password: z.string().min(8).max(128),
});
export type SignInWithEmail = z.infer<typeof signInWithEmailSchema>;

export const signUpWithEmailSchema = signInWithEmailSchema.extend({
  display_name: z.string().min(1).max(100),
});
export type SignUpWithEmail = z.infer<typeof signUpWithEmailSchema>;
